import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, inject, Output } from '@angular/core';
import { API, CampaignTypeDTO, CampaignTypeHierarchyDTO } from 'src/app/services/api/api';
import { ModalNavigationComponent } from 'src/app/services/gui/modal/modal-navigation-component';
import { ModalNavigationService } from 'src/app/services/gui/modal/modal-navigation.service';
import { UtilService } from 'src/app/services/util.service';
import { ErrorService } from '../../../services/error.service';
import { UserService } from '../../../services/user.service';
import { TagService } from '../../../services/tag.service';
import { CampaignTypeHierarchyDTOAndTags } from '../../../models/taggable.model';
import { PermissionService } from '../../../services/permission.service';
import { getItemId } from '../../../services/global/global-methods';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';
import { switchMap } from 'rxjs';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { FilterPanelConfig } from '@ngx-seatback/layout';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SbSplitButton } from '../../../services/global/global-interfaces';
import { RoutingID } from '../../../routes/sb-routing-enum';
import { RequestClass } from '../../../services/global/RequestClass';

type SplitButtonAction = 'campaigns' | 'edit';

@Component({
  selector: 'app-campaign-types',
  templateUrl: './campaign-types.component.html',
  styleUrls: ['./campaign-types.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CampaignTypesComponent extends RequestClass implements ModalNavigationComponent {
  private api = inject(API);
  private modalNavigation = inject(ModalNavigationService);
  public util = inject(UtilService);
  public errorHandler = inject(ErrorService);
  public userStatus = inject(UserService);
  public tagService = inject(TagService);
  public permissionService = inject(PermissionService);
  private readonly stringTransformation = inject(StringTransformationService);
  private readonly destroyRef = inject(DestroyRef);

  @Output() campaignTypeSelected: EventEmitter<CampaignTypeDTO> = new EventEmitter<CampaignTypeDTO>();

  // global variables
  isInIntegratedContext: boolean;
  isInModalContext: boolean;

  // table wrapper variables
  hierarchies: CampaignTypeHierarchyDTOAndTags[] = [];

  // filter variables
  exactMatch: SearchOptions = 'contains';
  search: string | null = null;
  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    hideTagSection: true,
    button: { visibleWithoutChanges: true },
  };

  protected readonly getItemId = getItemId;

  refreshFromServer() {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    const sub = this.api
      .ca_md_CampaignType_retrieveHierarchy(
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
        },
        this.pagination
      )
      .pipe(
        take(1),
        switchMap(campaignType => this.tagService.getAndAddTagEntitiesObs(this.tagService.TaggableDTO.CampaignType, campaignType)),
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (hierarchies: CampaignTypeHierarchyDTO[]) => {
          this.hierarchies = this.buildSplitButtons(hierarchies);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
          this.hierarchies = [];
        },
      });
    this.subs.push(sub);
  }

  selectCampaignType(item: CampaignTypeDTO) {
    const ret: CampaignTypeHierarchyDTO = this.api.createDTO('CampaignTypeHierarchyDTO');
    Object.assign(ret, item);
    delete ret.children;
    ret._type = 'CampaignTypeDTO';

    if (this.isInIntegratedContext) {
      this.campaignTypeSelected.emit(ret);
    }
    if (this.isInModalContext) {
      this.modalNavigation.result(ret);
    }
  }

  buildSplitButtons<T extends { id: any; name: any }>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      if (this.permissionService.hasPermissions([this.permissionService.Permission.campaign_list])) {
        item.sbButtons.push({
          icon: 'management',
          tooltip: 'lang.globals.campaigns',
          link: RoutingID.campaigns,
          queryParams: { campaignTypes: this.getItemId(item) },
        });
      }
      if (this.permissionService.hasPermissions([this.permissionService.Permission.campaign_type_edit])) {
        item.sbButtons.push({
          icon: 'edit',
          tooltip: 'lang.globals.editCampaignType',
          link: RoutingID.campaignTypeEdit,
          queryParams: { campaignTypeID: item?.id },
        });
      }
    });
    return result;
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
  }
}
